import { Game } from './catalog';

export interface PublicStoreAddress {
  city: {
    id: string;
    name: string;
    state: { id: string; name: string; code: string };
  };
}

export interface StoreAddress extends PublicStoreAddress {
  postalCode?: string;
  street?: string;
  number?: string;
  complement?: string;
  district?: string;
  city: PublicStoreAddress['city'] & {
    ibgeCode?: string;
    state: PublicStoreAddress['city']['state'] & { ibgeCode?: string };
  };
}

export interface PublicStore {
  id: string;
  name: string;
  slug: string;
  logoUrl?: string;
  description?: string;
  active: boolean;
  games: Game[];
  address?: PublicStoreAddress;
  openedAt?: string;
  completedSales: number;
  rating: { average: number; count: number };
}

export interface OwnerStore extends Omit<PublicStore, 'address'> {
  progress?: StoreProgress;
  documentCnpj?: string;
  corporateName?: string;
  stateRegistration?: string;
  freeShippingThreshold?: number;
  buyoutLimit?: number;
  shippingMethods: StoreShippingMethod[];
  address?: StoreAddress;
}

export interface AdminStore {
  id: string;
  name: string;
  slug: string;
  documentCnpj?: string;
  corporateName?: string;
  stateRegistration?: string;
  active: boolean;
  openedAt?: string;
}

export interface AdminStoresFilters {
  search?: string;
  status?: 'active' | 'inactive' | 'incomplete';
  page: number;
  limit: number;
}

export interface AdminStoresPage {
  items: AdminStore[];
  total: number;
  page: number;
  limit: number;
}

export type Store = PublicStore;

export const STORE_RANK = {
  COMMON: 'common',
  RARE: 'rare',
  SUPER: 'super',
  ULTRA: 'ultra',
  SECRET: 'secret',
  LEGENDARY: 'legendary',
} as const;

export type StoreRank = (typeof STORE_RANK)[keyof typeof STORE_RANK];

export interface StoreAchievement {
  code: string;
  label: string;
  description: string;
  featured: boolean;
  awardedAt: string;
}

export interface PublicStoreProgress {
  rank: StoreRank;
  label: string;
  achievements: StoreAchievement[];
}

export interface StoreProgress extends PublicStoreProgress {
  totalXp: number;
  currentRankMinXp: number;
  nextRank?: StoreRank;
  nextRankLabel?: string;
  nextRankXp?: number;
  xpRemaining: number;
  progressPercentage: number;
  hasUnseenPromotion: boolean;
  objectives: Array<{
    code: string;
    label: string;
    current: number;
    target: number;
    completed: boolean;
  }>;
}

export interface StoreInput {
  name: string;
  slug: string;
  logoUrl?: string;
  description?: string;
  documentCnpj?: string;
  corporateName?: string;
  stateRegistration?: string;
  freeShippingThreshold?: number;
  buyoutLimit?: number;
  shippingMethods: StoreShippingMethodInput[];
  gameIds: string[];
  address: {
    postalCode: string;
    street: string;
    number: string;
    complement?: string;
    district: string;
    cityIbgeCode: string;
    city: string;
    stateCode: string;
  };
}

export const STORE_SHIPPING_METHOD = {
  REGISTERED_MAIL: 'registered_mail',
  CORREIOS: 'correios',
  LOCAL_STORE: 'local_store',
} as const;

export type StoreShippingMethodCode =
  (typeof STORE_SHIPPING_METHOD)[keyof typeof STORE_SHIPPING_METHOD];

export interface StoreShippingMethod {
  code: StoreShippingMethodCode;
  active: boolean;
  fixedPrice?: number;
  priceUpTo100?: number;
  priceOver100?: number;
  pickupName?: string;
}

export type StoreShippingMethodInput = StoreShippingMethod;
